import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './Settings.css';

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function formatMoney(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(params.get('billing') === 'success' ? 'Welcome to your new plan!' : '');

  const reload = async () => {
    setError('');
    try {
      const data = await api.get('/api/billing/me');
      setSnap(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const toggleOverage = async () => {
    if (!snap?.overage_supported) return;
    setBusy('overage');
    setError('');
    try {
      const enabled = !snap.overage_enabled;
      await api.post('/api/billing/overage', { enabled });
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const openPortal = async () => {
    setBusy('portal');
    setError('');
    try {
      const { url } = await api.get('/api/billing/portal');
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  };

  return (
    <div className="settings-page">
      <nav className="settings-nav">
        <div className="nav-left">
          <button className="nav-wordmark" onClick={() => navigate('/portal')}>AEGENT</button>
          <span className="settings-crumb">/ Settings</span>
        </div>
        <div className="nav-right">
          <button className="nav-link" onClick={() => navigate('/portal')}>Dashboard</button>
          <button className="nav-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? '○' : '●'}
          </button>
        </div>
      </nav>

      <main className="settings-main">
        <motion.header
          className="settings-header"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="t-caption">Settings</p>
          <h1 className="t-h1">Account</h1>
        </motion.header>

        {flash && (
          <div className="settings-flash">
            <span>{flash}</span>
            <button onClick={() => setFlash('')} aria-label="Dismiss">×</button>
          </div>
        )}
        {error && <div className="settings-flash settings-flash-error">{error}</div>}

        {/* ACCOUNT ----------------------------------------------------- */}
        <section className="settings-section">
          <header className="settings-section-head">
            <h2 className="t-h3">Account</h2>
          </header>
          <div className="settings-row">
            <span className="settings-row-label">Username</span>
            <span className="settings-row-value">{user?.username || '—'}</span>
          </div>
          <div className="settings-row">
            <span className="settings-row-label">Email</span>
            <span className="settings-row-value">{user?.email || '—'}</span>
          </div>
        </section>

        {/* BILLING ----------------------------------------------------- */}
        <section className="settings-section">
          <header className="settings-section-head">
            <h2 className="t-h3">Billing & usage</h2>
            <div className="settings-section-actions">
              <button className="btn btn-sm" onClick={() => navigate('/pricing')}>Change plan</button>
              {snap?.plan?.billable && (
                <button
                  className="btn btn-sm"
                  disabled={busy === 'portal'}
                  onClick={openPortal}
                >
                  {busy === 'portal' ? '...' : 'Manage billing'}
                </button>
              )}
            </div>
          </header>

          {loading ? (
            <div className="settings-loading"><div className="loading-spinner" /></div>
          ) : snap && (
            <>
              <div className="settings-plan-card">
                <div className="settings-plan-card-head">
                  <p className="t-caption">Current plan</p>
                  <h3 className="t-h2">{snap.plan?.name || snap.plan_name}</h3>
                </div>
                <div className="settings-plan-meta">
                  <div>
                    <span className="settings-meta-label">Monthly credits</span>
                    <span className="settings-meta-value">{snap.credits?.grant != null ? formatNumber(snap.credits.grant) : 'Unlimited'}</span>
                  </div>
                  <div>
                    <span className="settings-meta-label">Burst ceiling</span>
                    <span className="settings-meta-value">
                      {snap.overage_enabled ? formatNumber(snap.burst_ceiling) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="settings-meta-label">Overage rate</span>
                    <span className="settings-meta-value">
                      {formatMoney(snap.plan?.overage_rate_cents)} / credit
                    </span>
                  </div>
                </div>
              </div>

              {/* monthly credit usage bar */}
              <div className="settings-usage">
                <div className="settings-usage-head">
                  <span className="t-caption">This month</span>
                  <span className="settings-usage-count">
                    {formatNumber(snap.credits?.used)}
                    {snap.credits?.grant != null && ` / ${formatNumber(snap.credits.grant)} credits`}
                    {snap.this_month?.overage > 0 && (
                      <span className="settings-usage-overage">
                        {' '}+ {formatNumber(snap.this_month.overage)} overage
                      </span>
                    )}
                  </span>
                </div>
                {snap.credits?.grant ? (
                  <div className="settings-usage-bar">
                    <div
                      className="settings-usage-fill"
                      style={{
                        width: `${Math.min(100, (Math.min(snap.credits.used, snap.credits.grant) / ((snap.overage_enabled && snap.burst_ceiling) ? snap.burst_ceiling : snap.credits.grant)) * 100)}%`,
                      }}
                    />
                    {snap.overage_enabled && snap.burst_ceiling && snap.this_month?.overage > 0 && (
                      <div
                        className="settings-usage-fill settings-usage-fill-overage"
                        style={{
                          left: `${(snap.credits.grant / snap.burst_ceiling) * 100}%`,
                          width: `${Math.min(100 - (snap.credits.grant / snap.burst_ceiling) * 100, (snap.this_month.overage / snap.burst_ceiling) * 100)}%`,
                        }}
                      />
                    )}
                  </div>
                ) : null}
              </div>

              {/* credits remaining */}
              <div className="settings-row">
                <span className="settings-row-label">Credits remaining</span>
                <span className="settings-row-value">
                  {snap.credits?.remaining != null ? formatNumber(snap.credits.remaining) : 'Unlimited'}
                </span>
              </div>

              {/* overage toggle */}
              <div className="settings-row settings-row-toggle">
                <div className="settings-row-stack">
                  <span className="settings-row-label">Overage</span>
                  <span className="settings-row-help">
                    {!snap.overage_supported
                      ? 'Your current plan doesn\'t support overage. Upgrade to enable.'
                      : !snap.overage_billable
                      ? 'Overage isn\'t available in this environment yet — Stripe metered pricing hasn\'t been configured for this plan.'
                      : `When on, your agents keep running past your monthly credits, up to 3× your grant. Each extra credit is billed at $${(snap.plan?.overage_rate_cents / 100).toFixed(2)}.`}
                  </span>
                </div>
                <button
                  className={`settings-toggle${snap.overage_enabled ? ' settings-toggle-on' : ''}`}
                  disabled={!snap.overage_supported || !snap.overage_billable || busy === 'overage'}
                  onClick={toggleOverage}
                  aria-label="Toggle overage"
                >
                  <span className="settings-toggle-knob" />
                </button>
              </div>
            </>
          )}
        </section>

        {/* DANGER ------------------------------------------------------ */}
        <section className="settings-section">
          <header className="settings-section-head">
            <h2 className="t-h3">Account actions</h2>
          </header>
          <div className="settings-row settings-row-actions">
            <span className="settings-row-label">Sign out</span>
            <button className="btn btn-sm" onClick={() => { logOut(); navigate('/'); }}>
              Sign out
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
